import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import {
  ClockIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  TrophyIcon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import HeadlineStat from "../../components/shared/HeadlineStat";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import ReportLayout, { ReportFilterSelect } from "./ReportLayout";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import {
  DISMANTLERS,
  MockDismantleEvent,
  MockPerson,
  PART_CATEGORIES,
  buildDismantleEvents,
  monthsAgo,
  today,
} from "./reportsMockData";

const PAGE_SIZE = 60;
const fmtInt = (n: number) => n.toLocaleString("en-GB");

const formatMinutes = (mins: number): string => {
  if (mins <= 0) return "0m";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

/**
 * Dismantler Report — lines of parts dismantled, filterable by dismantler
 * and date range. Also renders a per-dismantler leaderboard up top for the
 * "who's fastest this week?" eyeball check.
 */
export default function DismantlerReport() {
  const { setInfo } = usePageNameContext();
  const [dateRange, setDateRange] = useState({
    from: monthsAgo(3),
    to: today(),
  });
  const [dismantlerId, setDismantlerId] = useState("all");
  const [categoryId, setCategoryId] = useState("all");
  const [outcome, setOutcome] = useState("all");
  const [page, setPage] = useState(0);

  useEffect(() => {
    setInfo({
      name: "Dismantler Report",
      desc: "Parts dismantled by date range and dismantler, with time-taken totals",
    });
  }, [setInfo]);

  const allEvents = useMemo(
    () =>
      buildDismantleEvents(
        `${dateRange.from}T00:00:00Z`,
        `${dateRange.to}T23:59:59Z`
      ),
    [dateRange.from, dateRange.to]
  );

  const filtered = useMemo(() => {
    return allEvents.filter((e) => {
      if (dismantlerId !== "all" && e.dismantlerId !== dismantlerId) return false;
      if (categoryId !== "all" && e.partCategoryId !== categoryId) return false;
      if (outcome !== "all" && e.outcome !== outcome) return false;
      return true;
    });
  }, [allEvents, dismantlerId, categoryId, outcome]);

  useEffect(() => {
    setPage(0);
  }, [dateRange.from, dateRange.to, dismantlerId, categoryId, outcome]);

  const rollups = useMemo(() => {
    const byId = new Map<
      string,
      {
        person: MockPerson;
        parts: number;
        minutes: number;
        saleable: number;
        damaged: number;
        scrap: number;
        lastDate: string | null;
      }
    >();
    for (const p of DISMANTLERS) {
      byId.set(p.id, {
        person: p,
        parts: 0,
        minutes: 0,
        saleable: 0,
        damaged: 0,
        scrap: 0,
        lastDate: null,
      });
    }
    for (const e of filtered) {
      const row = byId.get(e.dismantlerId);
      if (!row) continue;
      row.parts += e.quantity;
      row.minutes += e.durationMinutes;
      if (e.outcome === "Saleable") row.saleable += e.quantity;
      if (e.outcome === "Damaged") row.damaged += e.quantity;
      if (e.outcome === "Scrap") row.scrap += e.quantity;
      if (!row.lastDate || e.dismantledDate > row.lastDate) {
        row.lastDate = e.dismantledDate;
      }
    }
    return [...byId.values()]
      .filter((r) => r.parts > 0)
      .sort((a, b) => b.parts - a.parts);
  }, [filtered]);

  const totals = useMemo(() => {
    let parts = 0;
    let minutes = 0;
    let saleable = 0;
    let damaged = 0;
    let scrap = 0;
    for (const e of filtered) {
      parts += e.quantity;
      minutes += e.durationMinutes;
      if (e.outcome === "Saleable") saleable += e.quantity;
      if (e.outcome === "Damaged") damaged += e.quantity;
      if (e.outcome === "Scrap") scrap += e.quantity;
    }
    return { parts, minutes, saleable, damaged, scrap };
  }, [filtered]);

  const avgMinutes =
    totals.parts === 0 ? 0 : Math.round(totals.minutes / totals.parts);
  const saleablePct =
    totals.parts === 0
      ? 0
      : Math.round((totals.saleable / totals.parts) * 100);

  const top = rollups[0];
  const maxParts = rollups.reduce((m, r) => Math.max(m, r.parts), 0) || 1;

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = useMemo(
    () => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filtered, page]
  );

  const csvRows = useMemo(
    () =>
      filtered.map((e) => ({
        dismantledDate: e.dismantledDate.slice(0, 10),
        dismantler: e.dismantler.name,
        donorVehicle: `${e.vehicle.year} ${e.vehicle.make} ${e.vehicle.model}`,
        donorStockNumber: e.vehicle.stockNumber,
        partCategory: e.partCategory,
        partName: e.partName,
        quantity: e.quantity,
        durationMinutes: e.durationMinutes,
        outcome: e.outcome,
        notes: e.notes,
      })),
    [filtered]
  );

  return (
    <ReportLayout
      title="Dismantler Report"
      description="Every part that's come off a vehicle in the selected window, attributed to the mechanic who took it off."
      icon={WrenchScrewdriverIcon}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      controls={
        <>
          <ReportFilterSelect
            label="Dismantler"
            value={dismantlerId}
            onChange={setDismantlerId}
            options={[
              { value: "all", label: "All dismantlers" },
              ...DISMANTLERS.map((p) => ({ value: p.id, label: p.name })),
            ]}
            width="11rem"
          />
          <ReportFilterSelect
            label="Category"
            value={categoryId}
            onChange={setCategoryId}
            options={[
              { value: "all", label: "All categories" },
              ...PART_CATEGORIES.map((c) => ({ value: c.id, label: c.name })),
            ]}
            width="11rem"
          />
          <ReportFilterSelect
            label="Outcome"
            value={outcome}
            onChange={setOutcome}
            options={[
              { value: "all", label: "All outcomes" },
              { value: "Saleable", label: "Saleable" },
              { value: "Damaged", label: "Damaged" },
              { value: "Scrap", label: "Scrap" },
            ]}
            width="9rem"
          />
        </>
      }
      csvRows={csvRows}
      csvFilename="dismantler-report"
      headlineStats={
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <HeadlineStat
            tone="blue"
            icon={CubeIcon}
            value={fmtInt(totals.parts)}
            label="Parts dismantled"
            sublabel={`${fmtInt(filtered.length)} events`}
          />
          <HeadlineStat
            tone="emerald"
            icon={ClockIcon}
            value={formatMinutes(totals.minutes)}
            label="Total time"
            sublabel={`${formatMinutes(avgMinutes)} avg / part`}
          />
          <HeadlineStat
            tone="violet"
            icon={CubeIcon}
            value={fmtInt(totals.saleable)}
            label="Saleable parts"
            sublabel={`${fmtInt(saleablePct)}% first-pass yield`}
          />
          <HeadlineStat
            tone="amber"
            icon={ExclamationTriangleIcon}
            value={fmtInt(totals.damaged)}
            label="Damaged"
            sublabel={`${fmtInt(totals.scrap)} scrapped`}
          />
          <HeadlineStat
            tone="cyan"
            icon={UserCircleIcon}
            value={fmtInt(rollups.length)}
            label="Active dismantlers"
            sublabel={`of ${fmtInt(DISMANTLERS.length)}`}
          />
          <HeadlineStat
            tone="rose"
            icon={TrophyIcon}
            value={top?.person.name ?? "—"}
            label="Top dismantler"
            sublabel={top ? `${fmtInt(top.parts)} parts` : undefined}
          />
        </div>
      }
    >
      {/* Leaderboard — quick "who's pulled the most parts" view before the
          big line-level table. */}
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <header className="mb-3 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            <TrophyIcon className="h-4 w-4" aria-hidden />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Dismantler leaderboard
            </h3>
            <p className="text-[11px] text-slate-500">
              Parts removed in the selected window
            </p>
          </div>
        </header>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead>
              <tr>
                <Th className="w-6"></Th>
                <Th>Dismantler</Th>
                <Th className="text-right">Parts</Th>
                <Th>Share</Th>
                <Th className="text-right">Time</Th>
                <Th className="text-right">Avg / part</Th>
                <Th className="text-right">Saleable</Th>
                <Th className="text-right">Damaged</Th>
                <Th className="text-right">Scrap</Th>
                <Th>Last activity</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rollups.map((r, index) => {
                const sharePct = Math.round((r.parts / maxParts) * 100);
                const avg =
                  r.parts === 0 ? 0 : Math.round(r.minutes / r.parts);
                return (
                  <tr key={r.person.id}>
                    <td className="pl-4 pr-1 py-2 align-middle">
                      <span
                        className={classNames(
                          "inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold",
                          index === 0
                            ? "bg-amber-100 text-amber-700"
                            : index === 1
                              ? "bg-slate-200 text-slate-700"
                              : index === 2
                                ? "bg-orange-100 text-orange-700"
                                : "bg-slate-100 text-slate-500"
                        )}
                      >
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary-100 text-[11px] font-semibold text-primary-700">
                          {r.person.initials}
                        </span>
                        <div className="flex flex-col leading-tight">
                          <span className="font-semibold text-slate-900">
                            {r.person.name}
                          </span>
                          <span className="text-[11px] text-slate-500">
                            {r.person.role}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900">
                      {fmtInt(r.parts)}
                    </td>
                    <td className="min-w-[9rem] px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full rounded-full bg-primary-500 transition-[width] duration-500"
                            style={{
                              width: `${Math.max(sharePct, r.parts > 0 ? 3 : 0)}%`,
                            }}
                          />
                        </div>
                        <span className="w-10 flex-none text-right text-[11px] tabular-nums text-slate-500">
                          {sharePct}%
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                      {formatMinutes(r.minutes)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                      {formatMinutes(avg)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                      {fmtInt(r.saleable)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700">
                      {fmtInt(r.damaged)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-rose-600">
                      {fmtInt(r.scrap)}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {r.lastDate
                        ? formatDateTime(r.lastDate, DateTime.DATE_MED)
                        : "—"}
                    </td>
                  </tr>
                );
              })}
              {rollups.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    No dismantling activity matches the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <Th>Date</Th>
                <Th>Dismantler</Th>
                <Th>Donor vehicle</Th>
                <Th>Part</Th>
                <Th>Category</Th>
                <Th className="text-right">Qty</Th>
                <Th className="text-right">Time</Th>
                <Th>Outcome</Th>
                <Th>Notes</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50/60">
                  <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                    {formatDateTime(e.dismantledDate, DateTime.DATETIME_MED)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary-100 text-[10px] font-semibold text-primary-700">
                        {e.dismantler.initials}
                      </span>
                      <span className="font-medium text-slate-700">
                        {e.dismantler.name}
                      </span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="flex flex-col leading-tight">
                      <span className="text-slate-700">
                        {e.vehicle.year} {e.vehicle.make} {e.vehicle.model}
                      </span>
                      <span className="font-mono text-[11px] text-slate-400">
                        {e.vehicle.registration} · {e.vehicle.stockNumber}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{e.partName}</td>
                  <td className="px-3 py-2 text-slate-500">{e.partCategory}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                    {e.quantity}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                    {formatMinutes(e.durationMinutes)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <OutcomePill outcome={e.outcome} />
                  </td>
                  <td className="px-3 py-2 text-slate-500">{e.notes}</td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    No dismantling events match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-slate-50">
                <tr className="text-sm font-semibold text-slate-700">
                  <td className="px-3 py-2" colSpan={5}>
                    Totals
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtInt(totals.parts)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMinutes(totals.minutes)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-2 text-sm">
            <p className="text-slate-500">
              Showing{" "}
              <span className="font-semibold text-slate-700">
                {fmtInt(page * PAGE_SIZE + 1)}–
                {fmtInt(Math.min((page + 1) * PAGE_SIZE, filtered.length))}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-slate-700">
                {fmtInt(filtered.length)}
              </span>
            </p>
            <div className="flex items-center gap-1">
              <PageButton
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Prev
              </PageButton>
              <span className="px-2 text-slate-500">
                Page {page + 1} / {pageCount}
              </span>
              <PageButton
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={page >= pageCount - 1}
              >
                Next
              </PageButton>
            </div>
          </div>
        )}
      </div>
    </ReportLayout>
  );
}

function Th({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={classNames(
        "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500",
        className ?? ""
      )}
    >
      {children}
    </th>
  );
}

function PageButton({
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...rest}
      className={classNames(
        "rounded-md border px-2 py-1 text-xs font-semibold transition",
        rest.disabled
          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
          : "border-slate-300 bg-white text-slate-700 hover:border-primary-300 hover:text-primary-700"
      )}
    >
      {children}
    </button>
  );
}

const OUTCOME_TONES: Record<MockDismantleEvent["outcome"], string> = {
  Saleable: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Damaged: "bg-amber-50 text-amber-700 ring-amber-200",
  Scrap: "bg-rose-50 text-rose-700 ring-rose-200",
};

function OutcomePill({ outcome }: { outcome: MockDismantleEvent["outcome"] }) {
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
        OUTCOME_TONES[outcome]
      )}
    >
      {outcome}
    </span>
  );
}
